import { useCallback, useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import {
  AddGoalSelect,
  AFF_METRIC_PREFIX,
  AVAILABLE_METRICS,
  CAMPUS_METRICS,
  CopyToFYSelect,
  CREATED_METRIC_PREFIX,
  FP_METRIC_PREFIX,
  type GoalRow,
  GoalRowInput,
  getMetricDef,
  NR_METRIC_PREFIX,
  SectionHeader,
  useSaveFlash,
} from "./shared";

export function MetricGoalsSection({
  ownerId,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [newMetricKey, setNewMetricKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { savedField, flashSaved } = useSaveFlash();
  const lastSavedRef = useRef<Record<string, string>>({});

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/targets?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        const rows: GoalRow[] = data.targets
          .filter(
            (t: { metric: string }) =>
              t.metric !== CAMPUS_METRICS.leads &&
              t.metric !== CAMPUS_METRICS.dealsWon &&
              !t.metric.startsWith(NR_METRIC_PREFIX) &&
              !t.metric.startsWith(AFF_METRIC_PREFIX) &&
              !t.metric.startsWith(CREATED_METRIC_PREFIX) &&
              !t.metric.startsWith(FP_METRIC_PREFIX),
          )
          .map((t: { metric: string; targetValue: number }) => ({
            metric: t.metric,
            value: String(t.targetValue),
          }));
        setGoals(rows);
        const snapshot: Record<string, string> = {};
        for (const r of rows) snapshot[r.metric] = r.value;
        lastSavedRef.current = snapshot;
        onDataChange(rows.length > 0);
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const usedMetrics = new Set(goals.map((g) => g.metric));
  const availableToAdd = AVAILABLE_METRICS.filter((m) => !usedMetrics.has(m.key));

  const sortedGoals = [...goals].sort((a, b) => {
    if (a.metric === newMetricKey) return 1;
    if (b.metric === newMetricKey) return -1;
    return (getMetricDef(a.metric)?.label ?? "").localeCompare(getMetricDef(b.metric)?.label ?? "");
  });

  function addGoal(metricKey: string) {
    setGoals((prev) => [...prev, { metric: metricKey, value: "" }]);
    setNewMetricKey(metricKey);
  }

  function updateGoalValue(metric: string, value: string) {
    setGoals((prev) => prev.map((g) => (g.metric === metric ? { ...g, value } : g)));
  }

  const saveMetric = useCallback(
    async (metric: string, value: string) => {
      if (value === (lastSavedRef.current[metric] ?? "")) return;

      const res = await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fiscalYear,
          targets: [{ metric, targetValue: value === "" ? null : Number(value) }],
        }),
      });

      if (res.ok) {
        lastSavedRef.current[metric] = value;
        if (newMetricKey === metric) setNewMetricKey(null);
        flashSaved(metric);
        const hasData = goals.some((g) => g.metric !== metric && g.value !== "") || value !== "";
        onDataChange(hasData);
      }
    },
    [ownerId, fiscalYear, newMetricKey, flashSaved, goals, onDataChange],
  );

  function handleBlur(metric: string) {
    const goal = goals.find((g) => g.metric === metric);
    if (goal) saveMetric(metric, goal.value);
  }

  function removeGoal(metric: string) {
    setGoals((prev) => {
      const updated = prev.filter((g) => g.metric !== metric);
      onDataChange(updated.some((g) => g.value !== ""));
      return updated;
    });
    fetch(`/api/owners/${ownerId}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, targets: [{ metric, targetValue: null }] }),
    });
    delete lastSavedRef.current[metric];
  }

  async function removeAll() {
    // Delete all metric goals (not campus targets) for this FY
    const toDelete = goals.map((g) => ({ metric: g.metric, targetValue: null }));
    if (toDelete.length > 0) {
      await fetch(`/api/owners/${ownerId}/targets`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fiscalYear, targets: toDelete }),
      });
    }
    setGoals([]);
    lastSavedRef.current = {};
    onDataChange(false);
    onRemove?.();
  }

  async function copyToFY(targetFY: number) {
    if (goals.length === 0) return;
    await fetch(`/api/owners/${ownerId}/targets`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fiscalYear: targetFY,
        targets: goals
          .filter((g) => g.value !== "")
          .map((g) => ({ metric: g.metric, targetValue: Number(g.value) })),
      }),
    });
  }

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="Metric Goals" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && goals.length > 0 && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <div className={loading ? "opacity-60" : ""}>
          {goals.length === 0 && !loading ? (
            <p className="text-sm text-muted-foreground py-2">
              No goals configured for {formatFY(fiscalYear)}.
              {!readOnly && " Add a metric target below."}
            </p>
          ) : (
            <div className="space-y-3">
              {sortedGoals.map((goal) => (
                <GoalRowInput
                  key={goal.metric}
                  goal={goal}
                  autoFocus={goal.metric === newMetricKey}
                  onChangeValue={(v) => updateGoalValue(goal.metric, v)}
                  onBlur={() => handleBlur(goal.metric)}
                  onRemove={readOnly ? undefined : () => removeGoal(goal.metric)}
                  savedField={savedField}
                  disabled={readOnly}
                />
              ))}
            </div>
          )}

          {!readOnly && <AddGoalSelect availableMetrics={availableToAdd} onAdd={addGoal} />}
        </div>
      </CardContent>
    </Card>
  );
}
